import React from 'react';
import {
  Sun, Moon, Cloud, CloudSun, CloudMoon, CloudRain, CloudSnow,
  CloudDrizzle, CloudFog, CloudLightning
} from 'lucide-react';

// ---------- Inline 30-min cache (separate from shared cacheUtils) ----------

const WEATHER_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

const getWeatherCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts < WEATHER_CACHE_TTL) return data;
    return null; // expired
  } catch {
    return null;
  }
};

const getStaleWeatherCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw).data;
  } catch {
    return null;
  }
};

const setWeatherCache = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch { /* quota exceeded — non-critical */ }
};

// ---------- WMO weather code mapping ----------

const weatherDescriptions = {
  0: 'Clear sky',
  1: 'Mainly clear',
  2: 'Partly cloudy',
  3: 'Overcast',
  45: 'Fog',
  48: 'Depositing rime fog',
  51: 'Light drizzle',
  53: 'Moderate drizzle',
  55: 'Dense drizzle',
  56: 'Light freezing drizzle',
  57: 'Dense freezing drizzle',
  61: 'Slight rain',
  63: 'Moderate rain',
  65: 'Heavy rain',
  66: 'Light freezing rain',
  67: 'Heavy freezing rain',
  71: 'Slight snowfall',
  73: 'Moderate snowfall',
  75: 'Heavy snowfall',
  77: 'Snow grains',
  80: 'Slight rain showers',
  81: 'Moderate rain showers',
  82: 'Violent rain showers',
  85: 'Slight snow showers',
  86: 'Heavy snow showers',
  95: 'Thunderstorm',
  96: 'Thunderstorm with slight hail',
  99: 'Thunderstorm with heavy hail',
};

/**
 * Get a human-readable weather description from a WMO code
 */
export const getWeatherDescription = (code) => {
  return weatherDescriptions[code] ?? 'Unknown';
};

/**
 * Return a lucide-react icon element for a WMO weather code
 * @param {number} code - WMO weather code
 * @param {number} size - Icon size in px (default 24)
 * @param {boolean} isNight - Whether it's currently night
 * @returns {JSX.Element}
 */
export const getWeatherIcon = (code, size = 24, isNight = false) => {
  const props = { size, className: 'inline-block' };

  if (code === 0 || code === 1) {
    return isNight
      ? <Moon {...props} className="inline-block text-gray-200" />
      : <Sun {...props} className="inline-block text-yellow-300" />;
  }
  if (code === 2) {
    return isNight
      ? <CloudMoon {...props} className="inline-block text-gray-300" />
      : <CloudSun {...props} className="inline-block text-yellow-200" />;
  }
  if (code === 3) return <Cloud {...props} className="inline-block text-gray-300" />;
  if (code === 45 || code === 48) return <CloudFog {...props} className="inline-block text-gray-400" />;
  if (code >= 51 && code <= 57) return <CloudDrizzle {...props} className="inline-block text-blue-300" />;
  if (code >= 61 && code <= 67) return <CloudRain {...props} className="inline-block text-blue-400" />;
  if (code >= 71 && code <= 77) return <CloudSnow {...props} className="inline-block text-white" />;
  if (code >= 80 && code <= 82) return <CloudRain {...props} className="inline-block text-blue-400" />;
  if (code >= 85 && code <= 86) return <CloudSnow {...props} className="inline-block text-white" />;
  if (code >= 95) return <CloudLightning {...props} className="inline-block text-yellow-400" />;

  return <Cloud {...props} className="inline-block text-gray-300" />;
};

/**
 * Return AQI label and Tailwind colour class
 */
export const getAqiLabel = (aqi) => {
  if (aqi == null) return { label: 'N/A', colorClass: 'text-white/50' };
  if (aqi <= 50) return { label: 'Good', colorClass: 'text-green-400' };
  if (aqi <= 100) return { label: 'Fair', colorClass: 'text-yellow-400' };
  if (aqi <= 150) return { label: 'Moderate', colorClass: 'text-orange-400' };
  if (aqi <= 200) return { label: 'Poor', colorClass: 'text-red-400' };
  return { label: 'Very Poor', colorClass: 'text-purple-400' };
};

/**
 * Fetch current weather + 6-hour forecast + air quality from Open-Meteo
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<Object|null>} weather data or null on failure
 */
export const fetchWeatherData = async (lat, lng) => {
  const cacheKey = `sunriseApp.weather.${lat.toFixed(2)},${lng.toFixed(2)}`;

  // Return fresh cache if available
  const cached = getWeatherCache(cacheKey);
  if (cached) return cached;

  const weatherUrl =
    `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
    `&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m,is_day` +
    `&hourly=temperature_2m,weather_code,is_day` +
    `&daily=temperature_2m_max,temperature_2m_min` +
    `&temperature_unit=celsius&wind_speed_unit=kmh&timezone=auto&forecast_days=2`;

  const aqUrl =
    `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${lat}&longitude=${lng}` +
    `&current=us_aqi,pm10,pm2_5`;

  try {
    const [weatherRes, aqRes] = await Promise.allSettled([
      fetch(weatherUrl).then(r => { if (!r.ok) throw new Error(r.status); return r.json(); }),
      fetch(aqUrl).then(r => { if (!r.ok) throw new Error(r.status); return r.json(); }),
    ]);

    if (weatherRes.status !== 'fulfilled') {
      throw new Error('Weather API failed');
    }

    const w = weatherRes.value;

    // Current conditions
    const current = {
      temperature: w.current.temperature_2m,
      feelsLike: w.current.apparent_temperature,
      humidity: w.current.relative_humidity_2m,
      weatherCode: w.current.weather_code,
      windSpeed: w.current.wind_speed_10m,
      isDay: w.current.is_day === 1,
    };

    // Next 6 hours from the hourly array
    // Open-Meteo returns local-timezone times (e.g. "2026-03-11T15:00").
    // Parse each as a Date and find the first slot after now.
    const nowMs = Date.now();
    let startIdx = w.hourly.time.findIndex(t => new Date(t).getTime() > nowMs);
    if (startIdx === -1) startIdx = Math.max(0, w.hourly.time.length - 6);

    const hourly = [];
    for (let i = 0; i < 6; i++) {
      const idx = startIdx + i;
      if (idx < w.hourly.time.length) {
        hourly.push({
          time: w.hourly.time[idx],
          temperature: w.hourly.temperature_2m[idx],
          weatherCode: w.hourly.weather_code[idx],
          isDay: w.hourly.is_day[idx] === 1,
        });
      }
    }

    // Daily high/low (today)
    const daily = {
      tempMax: w.daily.temperature_2m_max[0],
      tempMin: w.daily.temperature_2m_min[0],
    };

    // Air quality (optional — gracefully null)
    let airQuality = null;
    if (aqRes.status === 'fulfilled' && aqRes.value?.current) {
      const aq = aqRes.value.current;
      airQuality = {
        aqi: aq.us_aqi,
        pm10: aq.pm10,
        pm25: aq.pm2_5,
      };
    }

    const result = {
      current,
      hourly,
      daily,
      airQuality,
      units: {
        temperature: '\u00B0C',
        windSpeed: 'km/h',
      },
    };

    setWeatherCache(cacheKey, result);
    return result;
  } catch (err) {
    console.error('Weather fetch failed, trying stale cache:', err.message);
    const stale = getStaleWeatherCache(cacheKey);
    if (stale) return stale;
    return null;
  }
};
